import type {
  WhiteboardDrawingShape,
  WhiteboardMarkStyle,
  WhiteboardSceneElement,
  WhiteboardSpec,
  WhiteboardUpdateOp,
} from "./spec";

export interface WhiteboardMark {
  target: string;
  targetId?: string;
  style: WhiteboardMarkStyle;
  label?: string;
}

export interface WhiteboardDrawing {
  shape: WhiteboardDrawingShape;
  x: number;
  y: number;
  x2?: number;
  y2?: number;
  width?: number;
  height?: number;
  side?: "left" | "right" | "top" | "bottom";
  points?: Array<{ x: number; y: number }>;
  label?: string;
}

export interface WhiteboardConnection {
  from: string;
  to: string;
  fromId?: string;
  toId?: string;
  label?: string;
}

export interface WhiteboardRenderState {
  annotation: string | null;
  highlightTargets: string[];
  marks: WhiteboardMark[];
  connections: WhiteboardConnection[];
  drawings: WhiteboardDrawing[];
  elements: WhiteboardSceneElement[];
  lockedTileCount: number;
  revision: number;
}

export const EMPTY_WHITEBOARD_RENDER_STATE: WhiteboardRenderState = {
  annotation: null,
  highlightTargets: [],
  marks: [],
  connections: [],
  drawings: [],
  elements: [],
  lockedTileCount: 0,
  revision: 0,
};

export function resetWhiteboardRenderState(): WhiteboardRenderState {
  return {
    ...EMPTY_WHITEBOARD_RENDER_STATE,
    highlightTargets: [],
    marks: [],
    connections: [],
    drawings: [],
    elements: [],
  };
}

function optionKey(option: string): string {
  return option.trim().toLowerCase();
}

function addOption(options: string[], option: string): string[] {
  const trimmed = option.trim();
  if (!trimmed || options.some((existing) => optionKey(existing) === optionKey(trimmed))) {
    return options;
  }
  return [...options, trimmed];
}

function removeOption(options: string[], option: string): string[] {
  const target = optionKey(option);
  return options.filter((existing) => optionKey(existing) !== target);
}

function applyOptionMutation(spec: WhiteboardSpec, op: WhiteboardUpdateOp): WhiteboardSpec {
  if (op.op !== "add_option" && op.op !== "remove_option") return spec;
  if (spec.kind !== "gap_fill" && spec.kind !== "quick_choice") return spec;

  const previousOptions = spec.options;
  const nextOptions =
    op.op === "add_option"
      ? addOption(previousOptions, op.option)
      : removeOption(previousOptions, op.option);

  if (nextOptions === previousOptions) return spec;

  if (spec.kind === "gap_fill") {
    return { ...spec, options: nextOptions };
  }

  const previousAnswer =
    typeof spec.answerIndex === "number" ? previousOptions[spec.answerIndex] : undefined;
  const nextAnswerIndex = previousAnswer
    ? nextOptions.findIndex((option) => optionKey(option) === optionKey(previousAnswer))
    : -1;

  if (nextAnswerIndex >= 0) {
    return { ...spec, options: nextOptions, answerIndex: nextAnswerIndex };
  }

  const { answerIndex: _answerIndex, ...withoutAnswerIndex } = spec;
  return { ...withoutAnswerIndex, options: nextOptions };
}

function applyHintMutation(spec: WhiteboardSpec, op: WhiteboardUpdateOp): WhiteboardSpec {
  if (op.op !== "set_hint") return spec;
  if ("study" in spec && spec.study)
    return { ...spec, study: { ...spec.study, hint: op.hint.trim() } };
  if (spec.kind !== "gap_fill" && spec.kind !== "word_tiles") return spec;
  return { ...spec, hint: op.hint.trim() };
}

function applyEquationStepMutation(spec: WhiteboardSpec, op: WhiteboardUpdateOp): WhiteboardSpec {
  if (op.op !== "set_equation_step" || spec.kind !== "equation_balance") return spec;
  return {
    ...spec,
    lastStep: {
      stepId: op.stepId,
      operationId: op.operationId,
      operationLabel: op.operationLabel,
      workingEquation: op.workingEquation,
      visualDetail: op.visualDetail,
      fromEquation: spec.equation,
      toEquation: op.equation,
    },
    equation: op.equation,
    left: op.left,
    right: op.right,
    choices: op.choices,
    status: op.status,
  };
}

function addOrReplaceMark(marks: WhiteboardMark[], mark: WhiteboardMark): WhiteboardMark[] {
  const target = optionKey(mark.target);
  const targetId = mark.targetId ? optionKey(mark.targetId) : "";
  const next = marks.filter((existing) =>
    targetId && existing.targetId
      ? optionKey(existing.targetId) !== targetId
      : optionKey(existing.target) !== target
  );
  return [...next, mark].slice(-4);
}

function addDrawing(
  drawings: WhiteboardDrawing[],
  drawing: WhiteboardDrawing
): WhiteboardDrawing[] {
  return [...drawings, drawing].slice(-6);
}

function addOrReplaceElement(
  elements: WhiteboardSceneElement[],
  element: WhiteboardSceneElement
): WhiteboardSceneElement[] {
  const next = elements.filter((existing) => existing.id !== element.id);
  return [...next, element].slice(-4);
}

function addConnection(
  connections: WhiteboardConnection[],
  connection: WhiteboardConnection
): WhiteboardConnection[] {
  const from = optionKey(connection.from);
  const to = optionKey(connection.to);
  const fromId = connection.fromId ? optionKey(connection.fromId) : "";
  const toId = connection.toId ? optionKey(connection.toId) : "";
  const next = connections.filter(
    (existing) =>
      (fromId && existing.fromId
        ? optionKey(existing.fromId) !== fromId
        : optionKey(existing.from) !== from) ||
      (toId && existing.toId ? optionKey(existing.toId) !== toId : optionKey(existing.to) !== to)
  );
  return [...next, connection].slice(-4);
}

export function applyWhiteboardUpdates(
  spec: WhiteboardSpec,
  renderState: WhiteboardRenderState,
  ops: WhiteboardUpdateOp[]
): { spec: WhiteboardSpec; renderState: WhiteboardRenderState } {
  let nextSpec = spec;
  let nextRenderState: WhiteboardRenderState = {
    ...renderState,
    highlightTargets: [],
    revision: renderState.revision + 1,
  };

  for (const op of ops) {
    if (op.op === "highlight") {
      const highlightTargets = op.targetId
        ? [op.targetId.trim(), op.target.trim()]
        : [op.target.trim()];
      nextRenderState = {
        ...nextRenderState,
        highlightTargets: [...nextRenderState.highlightTargets, ...highlightTargets].filter(
          Boolean
        ),
      };
      continue;
    }
    if (op.op === "mark") {
      const target = op.target.trim();
      if (target) {
        nextRenderState = {
          ...nextRenderState,
          marks: addOrReplaceMark(nextRenderState.marks, {
            target,
            targetId: op.targetId?.trim() || undefined,
            style: op.style ?? "underline",
            label: op.label?.trim() || undefined,
          }),
        };
      }
      continue;
    }
    if (op.op === "clear_marks") {
      nextRenderState = { ...nextRenderState, marks: [] };
      continue;
    }
    if (op.op === "connect") {
      const from = op.from.trim();
      const to = op.to.trim();
      if (from && to) {
        nextRenderState = {
          ...nextRenderState,
          connections: addConnection(nextRenderState.connections, {
            from,
            to,
            fromId: op.fromId?.trim() || undefined,
            toId: op.toId?.trim() || undefined,
            label: op.label?.trim() || undefined,
          }),
        };
      }
      continue;
    }
    if (op.op === "clear_connections") {
      nextRenderState = { ...nextRenderState, connections: [] };
      continue;
    }
    if (op.op === "draw") {
      if (op.shape === "path") {
        const firstPoint = op.points[0];
        nextRenderState = {
          ...nextRenderState,
          drawings: addDrawing(nextRenderState.drawings, {
            shape: op.shape,
            x: firstPoint?.x ?? 0,
            y: firstPoint?.y ?? 0,
            points: op.points,
            label: op.label?.trim() || undefined,
          }),
        };
        continue;
      }

      const drawing: WhiteboardDrawing = {
        shape: op.shape,
        x: op.x,
        y: op.y,
        label: op.label?.trim() || undefined,
      };
      if ("x2" in op && "y2" in op) {
        drawing.x2 = op.x2;
        drawing.y2 = op.y2;
      } else if ("width" in op && "height" in op) {
        drawing.width = op.width;
        drawing.height = op.height;
        if (op.shape === "bracket") {
          drawing.side = op.side ?? "left";
        }
      }
      nextRenderState = {
        ...nextRenderState,
        drawings: addDrawing(nextRenderState.drawings, drawing),
      };
      continue;
    }
    if (op.op === "clear_drawings") {
      nextRenderState = { ...nextRenderState, drawings: [] };
      continue;
    }
    if (op.op === "annotate") {
      nextRenderState = { ...nextRenderState, annotation: op.note.trim() };
      continue;
    }
    if (op.op === "add_element") {
      nextRenderState = {
        ...nextRenderState,
        elements: addOrReplaceElement(nextRenderState.elements, op.element),
      };
      continue;
    }
    if (op.op === "remove_element") {
      nextRenderState = {
        ...nextRenderState,
        elements: nextRenderState.elements.filter((element) => element.id !== op.elementId),
      };
      continue;
    }
    if (op.op === "clear_elements") {
      nextRenderState = { ...nextRenderState, elements: [] };
      continue;
    }
    if (op.op === "set_equation_step") {
      nextSpec = applyEquationStepMutation(nextSpec, op);
      continue;
    }
    if (op.op === "lock_tiles") {
      nextRenderState = { ...nextRenderState, lockedTileCount: op.count };
      continue;
    }
    nextSpec = applyHintMutation(applyOptionMutation(nextSpec, op), op);
  }

  return { spec: nextSpec, renderState: nextRenderState };
}
